import type { NumberBlock } from './types'
import { randInt } from './rng'
import { hasAnyMove } from './rules'

// ─────────────────────────────────────────────────────────────
// 초기 격자(보드) 생성 튜닝 파라미터
// 게임 규칙/진행/점수 로직과 무관 — 여기 값만 바꾸면 난이도 분포가 조정됨.
// ─────────────────────────────────────────────────────────────

/** 숫자를 3구간(1~3 / 4~6 / 7~9)으로 나눈 생성 비율. 기본 5:3:2. */
export const BUCKET_RATIOS: readonly [number, number, number] = [5, 3, 2]

/** 각 구간에 속하는 숫자들. 구간 내에서는 균등 분포. */
export const DIGIT_BUCKETS: readonly (readonly number[])[] = [
  [1, 2, 3],
  [4, 5, 6],
  [7, 8, 9],
]

/**
 * 안전망: 그리디 추정으로 제거 가능한 셀 비율이 이 값 미만이면 재생성.
 * 분포를 크게 훼손하지 않도록 보수적으로 설정(대부분의 보드는 이보다 높음).
 */
export const MIN_CLEARABLE_RATIO = 0.5

/** BUCKET_RATIOS/DIGIT_BUCKETS 로부터 숫자 1~9 각각의 가중치(index = digit-1) 계산. */
export function buildDigitWeights(
  ratios: readonly number[] = BUCKET_RATIOS,
  buckets: readonly (readonly number[])[] = DIGIT_BUCKETS,
): number[] {
  const weights: number[] = new Array(9).fill(0)
  buckets.forEach((digits, bi) => {
    const perDigit = ratios[bi] / digits.length // 구간 내 균등
    for (const d of digits) weights[d - 1] = perDigit
  })
  return weights
}

/** 기본 가중치(5:3:2). 튜닝 시 buildDigitWeights(...)로 다른 값을 넘길 수 있음. */
export const DIGIT_WEIGHTS: readonly number[] = buildDigitWeights()

/** 가중치 배열에 따라 숫자 1~9 중 하나를 결정적으로 샘플링. */
export function sampleDigit(rng: () => number, weights: readonly number[] = DIGIT_WEIGHTS): number {
  let total = 0
  for (const w of weights) total += w
  let t = rng() * total
  for (let i = 0; i < weights.length; i++) {
    t -= weights[i]
    if (t < 0) return i + 1
  }
  return weights.length // 부동소수 경계 방어
}

// ─────────────────────────────────────────────────────────────
// 생성 함수
// ─────────────────────────────────────────────────────────────

/** (개선 전 기준선) 순수 균등 랜덤 1~9. 시뮬레이션 비교/회귀용으로 보존. */
export function generateBlocksUniform(size: number, rng: () => number): NumberBlock[] {
  const n = size * size
  const blocks: NumberBlock[] = []
  for (let i = 0; i < n; i++) {
    blocks.push({ id: i, value: randInt(rng, 8) + 1, x: i % size, y: Math.floor(i / size) })
  }
  return blocks
}

/** 구간 가중 랜덤으로 격자 채움(총합 보정 전). */
export function generateBlocksWeighted(
  size: number, rng: () => number, weights: readonly number[] = DIGIT_WEIGHTS,
): NumberBlock[] {
  const n = size * size
  const blocks: NumberBlock[] = []
  for (let i = 0; i < n; i++) {
    blocks.push({ id: i, value: sampleDigit(rng, weights), x: i % size, y: Math.floor(i / size) })
  }
  return blocks
}

/**
 * 격자 전체 합을 10의 배수로 최소 변경 보정.
 * 총 변경량은 이론적 하한 min(r, 10-r) (r = sum%10) 과 일치하며,
 * 가능하면 한 칸만 바꿔(분포 훼손 최소) 처리한다. 값은 항상 1~9 유지.
 * rng 를 사용하되 결정적(같은 시드 → 같은 결과).
 */
export function adjustBoardSumToMultipleOf10(blocks: NumberBlock[], rng: () => number): NumberBlock[] {
  const sum = blocks.reduce((s, b) => s + b.value, 0)
  const r = sum % 10
  if (r === 0) return blocks

  const up = 10 - r        // 총합을 늘려서 맞추는 비용
  const down = r           // 총합을 줄여서 맞추는 비용
  const dir = down <= up ? -1 : 1 // 더 적게 바꾸는 방향
  let remaining = Math.min(down, up) // 필요한 총 변경량(=이론적 하한)

  // 1) 한 칸으로 전부 흡수 가능하면 그 칸만 변경(변경 셀 수 최소화)
  const canAbsorb = (b: NumberBlock) => dir > 0 ? b.value + remaining <= 9 : b.value - remaining >= 1
  const single = pickEligible(blocks, canAbsorb, rng)
  if (single >= 0) {
    blocks[single].value += dir * remaining
    return blocks
  }

  // 2) 아니면 한 스텝씩 여러 칸에 분산(각 칸을 ±1). 총 변경량은 동일하게 remaining.
  while (remaining > 0) {
    const canStep = (b: NumberBlock) => dir > 0 ? b.value < 9 : b.value > 1
    const idx = pickEligible(blocks, canStep, rng)
    if (idx < 0) break // 이론상 도달 불가(모든 칸이 극단값) — 방어
    blocks[idx].value += dir
    remaining--
  }
  return blocks
}

/** 조건을 만족하는 셀 중 하나를 rng로 결정적으로 선택(없으면 -1). */
function pickEligible(blocks: NumberBlock[], ok: (b: NumberBlock) => boolean, rng: () => number): number {
  const cands: number[] = []
  for (let i = 0; i < blocks.length; i++) if (ok(blocks[i])) cands.push(i)
  if (cands.length === 0) return -1
  return cands[randInt(rng, cands.length - 1)]
}

/**
 * 프로덕션 생성 함수: 구간 가중 랜덤 + 총합 10배수 보정.
 * 시그니처/결정성은 기존과 동일(같은 시드 → 같은 보드).
 */
export function generateBlocks(size: number, rng: () => number): NumberBlock[] {
  const blocks = generateBlocksWeighted(size, rng)
  return adjustBoardSumToMultipleOf10(blocks, rng)
}

/**
 * 제거 가능 셀 수의 그리디 추정.
 * 합=10 직사각형 중 셀 수가 가장 많은 것을 반복 제거(실제 규칙과 동일한 정의).
 * 규칙 로직은 건드리지 않고 생성 품질 판정용으로만 사용.
 */
export function estimateClearableCount(blocks: NumberBlock[], size: number): number {
  const g = new Int32Array(size * size)
  for (const b of blocks) g[b.y * size + b.x] = b.value
  const W = size + 1
  const removeBest = (): number => {
    const ps = new Int32Array(W * W)
    const pc = new Int32Array(W * W)
    for (let rr = 0; rr < size; rr++) {
      for (let cc = 0; cc < size; cc++) {
        const v = g[rr * size + cc]
        ps[(rr + 1) * W + (cc + 1)] = v + ps[rr * W + (cc + 1)] + ps[(rr + 1) * W + cc] - ps[rr * W + cc]
        pc[(rr + 1) * W + (cc + 1)] = (v !== 0 ? 1 : 0) + pc[rr * W + (cc + 1)] + pc[(rr + 1) * W + cc] - pc[rr * W + cc]
      }
    }
    const sum = (r1: number, c1: number, r2: number, c2: number) =>
      ps[(r2 + 1) * W + (c2 + 1)] - ps[r1 * W + (c2 + 1)] - ps[(r2 + 1) * W + c1] + ps[r1 * W + c1]
    const cnt = (r1: number, c1: number, r2: number, c2: number) =>
      pc[(r2 + 1) * W + (c2 + 1)] - pc[r1 * W + (c2 + 1)] - pc[(r2 + 1) * W + c1] + pc[r1 * W + c1]
    let best: [number, number, number, number] | null = null
    let bestN = 0
    for (let r1 = 0; r1 < size; r1++)
      for (let c1 = 0; c1 < size; c1++)
        for (let r2 = r1; r2 < size; r2++)
          for (let c2 = c1; c2 < size; c2++)
            if (sum(r1, c1, r2, c2) === 10) {
              const n = cnt(r1, c1, r2, c2)
              if (n > bestN) { bestN = n; best = [r1, c1, r2, c2] }
            }
    if (!best) return 0
    const [r1, c1, r2, c2] = best
    for (let rr = r1; rr <= r2; rr++) for (let cc = c1; cc <= c2; cc++) g[rr * size + cc] = 0
    return bestN
  }
  let cleared = 0
  for (;;) {
    const n = removeBest()
    if (n === 0) break
    cleared += n
  }
  return cleared
}

/**
 * 유효(최소 한 수 존재) + 제거 가능 비율 임계 이상인 보드를 생성.
 * 실패 시 재생성하되 상한을 둔다. 상한 도달 시 마지막 후보 반환(총합 보정은 항상 적용됨).
 */
export function generateValidBlocks(size: number, rng: () => number, maxAttempts = 100): NumberBlock[] {
  const total = size * size
  let last = generateBlocks(size, rng)
  for (let a = 0; a < maxAttempts; a++) {
    if (hasAnyMove(last, size) && estimateClearableCount(last, size) / total >= MIN_CLEARABLE_RATIO) {
      return last
    }
    last = generateBlocks(size, rng)
  }
  return last
}
